import { action, computed, makeObservable, observable } from 'mobx';

import ApiService from '../services/ApiService';
import { amountAsNumber, validateAmount } from '../util/ValidationUtil';

import { IModalStore, ModalType } from './ModalStore';
import { Loadable } from './models/Loadable';
import { PosPayment } from './models/PosPayment';
import { PosPaymentRequest } from './models/PosPaymentRequest';
import { IRouteStore } from './RouteStore';
import { ISsoStore } from './SsoStore';

export interface IPosPaymentStore {
  readonly payment: Loadable<PosPayment>;
  readonly amount: string;
  readonly referenceNumber: string;
  readonly amountFieldErrorMsg: string;
  readonly amountFieldHasError: boolean;

  activatePaymentModal(amount: number, referenceNumber?: string): void;

  changeAmount(amount: string): void;

  createPayment(): void;
}

export default class PosPaymentStore implements IPosPaymentStore {
  @observable
  public payment: Loadable<PosPayment> = new Loadable<PosPayment>();
  @observable
  public amount: string;
  @observable
  public referenceNumber: string;

  constructor(
    private apiService: ApiService,
    private routeStore: IRouteStore,
    private modalStore: IModalStore,
    private ssoStore: ISsoStore
  ) {
    makeObservable(this);
  }

  @action
  public activatePaymentModal(amount: number, referenceNumber?: string): void {
    this.modalStore.openModal(ModalType.PosPaymentModal);
    this.referenceNumber = referenceNumber;
    this.amount = amount.toString();
  }

  @computed
  public get amountFieldErrorMsg(): string {
    const validationResult = validateAmount(this.amount);
    return validationResult && `invoices.pos-payment-modal.errors.${validationResult}`;
  }

  @action.bound
  public changeAmount(newAmount: string): void {
    this.amount = newAmount === null || newAmount === undefined ? '0,00' : newAmount;
  }

  @action.bound
  public createPayment(): void {
    const paymentRequest: PosPaymentRequest = {
      referenceNumber: this.referenceNumber,
      amount: amountAsNumber(this.amount)
    };
    const paymentPromise: Promise<PosPayment> = this.apiService.createPayment(this.ssoStore.customer.customerId, paymentRequest);
    paymentPromise.then((payment) => this.routeStore.goToExternalUrl(payment.paymentUrl));
    this.payment = Loadable.toLoadable(paymentPromise);
  }

  @computed
  public get amountFieldHasError(): boolean {
    return this.amountFieldErrorMsg !== '';
  }
}
